import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderOpen, Loader2, Search } from "lucide-react";
import ClientShell from "./ClientShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useFileIndex, useFileUploadContext, useCreateFileRoomLaunch, useRemoveFileLink } from "@/lib/fileQueries";
import FileUploadDialog from "./files/FileUploadDialog";
import ImportPreviewDialog from "./files/ImportPreviewDialog";
import FileCard from "./files/FileCard";
import FilePreviewDrawer from "./files/FilePreviewDrawer";
import ReplaceFileInline from "./files/ReplaceFileInline";
import RequestFeedbackDialog from "./feedback/RequestFeedbackDialog";
import FeatureOnboarding from "./onboarding/FeatureOnboarding";
import { MyAuditResults } from "./shared/MyAuditResults";

/**
 * Master v5 Prompt 11 — the General Files page: the permission-filtered
 * FileIndex across the whole Workspace. Every upload here goes through
 * FileUploadDialog's classification picker (never a context-free file).
 */
export default function FilesPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isPending, refetch } = useFileIndex(workspaceId);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const removeLink = useRemoveFileLink(workspaceId);
  const createRoomLaunch = useCreateFileRoomLaunch(workspaceId);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [importReady, setImportReady] = useState(null); // { workspaceId, importId, draft }
  const [previewFasId, setPreviewFasId] = useState(null);
  const [replacingEntry, setReplacingEntry] = useState(null);
  const [query, setQuery] = useState("");
  const [feedbackPrefill, setFeedbackPrefill] = useState(null);

  const uploadMembers = (uploadContext?.team_members || []).map((m) => ({ member_id: m.membership_id, member_name: m.display_name }));

  const items = (data?.items || []).filter((entry) =>
    !query.trim() || entry.asset.original_filename.toLowerCase().includes(query.trim().toLowerCase())
  );

  // Deep link support (e.g. a Coach Today "file_upload" task) — auto-open
  // the referenced file's preview once the index has loaded.
  useEffect(() => {
    const targetFasId = searchParams.get("fas_id");
    if (targetFasId && items.some((i) => i.asset.fas_id === targetFasId)) {
      setPreviewFasId(targetFasId);
    }
  }, [searchParams, items]);
  const isTeamWorkspace = uploadContext?.access_type === "team";
  const actorRole = uploadContext?.workspace_role === "coach" ? "coach" : "client_member";

  function handleOpen(entry) {
    setPreviewFasId(entry.asset.fas_id);
  }

  async function handleOpenRoom(fasId) {
    try {
      const res = await createRoomLaunch.mutateAsync(fasId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't open this file in a Room");
    }
  }

  async function handleRemoveLink(fasId, flnId) {
    try {
      await removeLink.mutateAsync({ fasId, flnId });
    } catch {
      toast.error("Couldn't remove that context");
    }
  }

  function handleRequestFeedback(fasId) {
    const entry = items.find((i) => i.asset.fas_id === fasId);
    setFeedbackPrefill({ target_type: "file_asset", target_id: fasId, label: entry?.asset.original_filename });
  }

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="files-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-ink flex items-center gap-2"><FolderOpen size={22} className="text-terracotta" /> Files</h1>
            <p className="text-ink-soft text-sm mt-1">Everything uploaded across this Workspace, filtered to what you can see.</p>
          </div>
          <Button onClick={() => setUploadOpen(true)} data-testid="files-page-upload-btn">
            <Plus size={15} className="mr-1.5" /> Upload
          </Button>
        </div>

        <FeatureOnboarding
          workspaceId={workspaceId}
          featureKey="files"
          title="Welcome to Files"
          body={isTeamWorkspace
            ? "Upload a recording or document here, or right from a Practice, Session or Idea Bank item. Choose who should see it — just you & your coach, or your whole team."
            : "Upload a recording or document here, or right from a Practice or Session item — every upload is private between you and your coach."}
        />

        {!isPending && (data?.items || []).length > 0 && (
          <div className="relative mb-4 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <Input
              value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search files by name…"
              className="pl-8" data-testid="files-page-search-input"
            />
          </div>
        )}

        <MyAuditResults workspaceId={workspaceId} />

        {isPending ? (
          <div className="flex items-center gap-2 text-ink-soft" data-testid="files-page-loading"><Loader2 size={16} className="animate-spin" /> Loading files…</div>
        ) : items.length === 0 ? (
          <p className="text-ink-soft text-sm" data-testid="files-page-empty">
            {query.trim() ? `No files match "${query.trim()}".` : "No files yet. Upload one to get started."}
          </p>
        ) : (
          <div className="space-y-3" data-testid="files-page-list">
            {items.map((entry) => (
              <FileCard
                key={entry.asset.fas_id} entry={entry} workspaceId={workspaceId}
                onOpen={handleOpen} onOpenWorkDocument={(wdocId) => navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${wdocId}`)} onReplace={setReplacingEntry}
                onRemoveLink={handleRemoveLink} onOpenRoom={handleOpenRoom} onRequestFeedback={handleRequestFeedback}
              />
            ))}
          </div>
        )}

        <FileUploadDialog
          open={uploadOpen} onOpenChange={setUploadOpen} workspaceId={workspaceId}
          actorRole={actorRole} isTeamWorkspace={isTeamWorkspace} teamMembers={uploadContext?.team_members || []}
          onUploaded={() => refetch()}
          onImportReady={(payload) => { setUploadOpen(false); setImportReady({ workspaceId, ...payload }); }}
        />
        {importReady && (
          <ImportPreviewDialog
            open={!!importReady} onOpenChange={(o) => { if (!o) { setImportReady(null); refetch(); } }}
            workspaceId={importReady.workspaceId} importId={importReady.importId} initialDraft={importReady.draft}
            members={uploadMembers} isTeamWorkspace={isTeamWorkspace}
          />
        )}
        <FilePreviewDrawer open={!!previewFasId} onOpenChange={(o) => !o && setPreviewFasId(null)} workspaceId={workspaceId} fasId={previewFasId} actorRole={actorRole} />
        {replacingEntry && (
          <ReplaceFileInline workspaceId={workspaceId} entry={replacingEntry} onDone={() => { setReplacingEntry(null); refetch(); }} />
        )}
        <RequestFeedbackDialog
          open={!!feedbackPrefill} onOpenChange={(o) => !o && setFeedbackPrefill(null)} workspaceId={workspaceId}
          isTeamWorkspace={isTeamWorkspace} prefill={feedbackPrefill}
        />
      </div>
    </ClientShell>
  );
}
